import React, { useCallback, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "react-loading-skeleton/dist/skeleton.css";
import "swiper/css";

import { useLocationContext } from "../../contexts/locationContext";
import { handleGetLeagues } from "../../shared/controllers/leagueController";
import { useUserContext } from "../../userDashboard/contexts/userContext";
import LoadingTable from "../../utils/loadingTable";
import { toIso3 } from "../../utils/locationStorage";

const encodeId = (id: string | number) => {
    return encodeURIComponent(btoa(id.toString()));
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

interface League {
    id: number;
    name: string;
    logo?: string;
    description?: string;
    city?: string;
    state?: string;
    country?: string;
    start_date?: string;
    created_at?: string;
    status?: string;
    is_active?: boolean | number;
}

const LeagueLanding: React.FC = () => {
    const [leagues, setLeagues] = useState<League[]>([]);
    const [loading, setLoading] = useState(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isAuthenticated = !!localStorage.getItem("saca_token");
    const { location: storedLocation } = useLocationContext();
    const userContext = useUserContext();
    const userProfile = userContext?.userProfile ?? null;
    const storedCountryCode = storedLocation?.countryCode || "";
    const storedState = storedLocation?.state || "";
    const storedCity = storedLocation?.city || "";

    const effectiveCountryCode = useMemo(() => {
        const raw =
            userProfile?.country_code ||
            userProfile?.country ||
            storedCountryCode;
        return toIso3(raw as string) || "";
    }, [
        userProfile?.country_code,
        userProfile?.country,
        storedCountryCode,
    ]);

    const normalizeLeagues = (response: unknown): League[] => {
        if (!response) return [];
        if (Array.isArray(response)) return response;
        const payload = asRecord(response);
        if (Array.isArray(payload.data)) return payload.data as League[];
        const nested = asRecord(payload.data).data;
        if (Array.isArray(nested)) return nested as League[];
        return [];
    };

    const getLocationLabel = (item: League) => {
        if (!item) return "N/A";
        const parts = [item.city, item.state, item.country].filter(Boolean);
        if (parts.length) return parts.join(", ");
        return "N/A";
    };

    const fetchLeagues = useCallback(async () => {
        setLoading(true);
        try {
            const params: Record<string, string> = {};
            if (effectiveCountryCode) {
                params.country_code = effectiveCountryCode;
            }
            if (storedState) {
                params.state = storedState;
            }
            if (storedCity) {
                params.city = storedCity;
            }

            const data = await handleGetLeagues('user', params);
            const list = normalizeLeagues(data);
            setLeagues(list.slice(0, 5));
        } catch (error) {
            setLeagues([]);
        } finally {
            setLoading(false);
        }
    }, [
        effectiveCountryCode,
        storedCity,
        storedState,
    ]);

    useEffect(() => {
        fetchLeagues();
    }, [fetchLeagues]);

    return (
        <>
            <div
                id="league-section"
                className="flex flex-col items-start justify-center py-12 md:py-16 w-full px-4 md:px-14"
            >
                <h3>Our Leagues</h3>
                <h2 className="mt-3 font-Manrope">Join competitive leagues</h2>

                <div className="hidden md:block w-full border border-gray-200 rounded-[10px] mt-8 overflow-x-auto">
                    <table className="min-w-full font-Manrope">
                        <thead>
                            <tr>
                                <th className="py-4 px-6 text-left text-sm font-bold text-black">
                                    S/N
                                </th>
                                <th className="py-4 px-6 text-left text-sm font-bold text-black">
                                    League Name
                                </th>
                                <th className="py-4 px-6 text-left text-sm font-bold text-black">
                                    Location
                                </th>
                                <th className="py-4 px-6 text-left text-sm font-bold text-black">
                                    Start Date
                                </th>
                                <th className="py-4 px-6 text-left text-sm font-bold text-black">
                                    Status
                                </th>
                                <th className="py-4 px-6 text-left text-sm font-bold text-black">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={6}>
                                        <LoadingTable rows={6} columns={6} />
                                    </td>
                                </tr>
                            ) : leagues.length === 0 ? (
                                <tr className=" w-full">
                                    <td
                                        colSpan={6}
                                        className="px-4 py-3 text-center font-Outfit text-[#667085] text-sm w-full"
                                    >
                                        There are no leagues yet.
                                    </td>
                                </tr>
                            ) : (
                                leagues.map((item, index) => (
                                    <tr key={index} className="border-t">
                                        <td className="py-4 px-6 text-sm text-[#5F6D7E] font-medium font-Manrope">
                                            0{index + 1}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-[#5F6D7E] font-medium font-Manrope">
                                            <div className="flex items-center space-x-3">
                                                {item.logo && (
                                                    <span
                                                        style={{
                                                            backgroundImage: `url(${item.logo})`,
                                                            backgroundPosition: "center",
                                                            backgroundSize: "cover",
                                                        }}
                                                        className="w-8 h-8 rounded-full bg-[#f5f5f5]"
                                                    ></span>
                                                )}
                                                <div>
                                                    <p className="font-semibold">{item.name}</p>
                                                    {item.description && (
                                                        <p className="text-xs text-gray-500">
                                                            {item.description.length > 50
                                                                ? `${item.description.substring(0, 50)}...`
                                                                : item.description}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 px-6 text-sm text-[#5F6D7E] font-medium font-Manrope">
                                            {getLocationLabel(item)}
                                        </td>
                                        <td className="py-4 px-6 text-sm text-[#5F6D7E] font-medium font-Manrope">
                                            {item.start_date || item.created_at ?
                                                new Date(item.start_date || item.created_at || "").toLocaleDateString() :
                                                "N/A"
                                            }
                                        </td>
                                        <td className="py-4 px-6 text-sm text-[#5F6D7E] font-medium font-Manrope">
                                            <span
                                                className={`px-3 py-1 rounded-full text-xs font-semibold ${item.status === "active" || item.is_active
                                                    ? "bg-green-100 text-green-800"
                                                    : "bg-gray-100 text-gray-800"
                                                    }`}
                                            >
                                                {item.status === "active" || item.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6 text-sm text-[#5F6D7E] font-medium font-Manrope">
                                            <Link
                                                to={`/leagues?id=${encodeId(item.id)}&name=${encodeURIComponent(item.name)}`}
                                                className="text-[#317333] font-bold hover:underline"
                                            >
                                                View Details
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile view - Swiper */}
                <Swiper
                    className="block md:hidden w-full mt-8"
                    spaceBetween={16}
                    slidesPerView={1.2}
                    breakpoints={{
                        640: {
                            slidesPerView: 1.5,
                        },
                    }}
                    modules={[Pagination]}
                    pagination={{
                        el: ".leagues-pagination",
                        clickable: true,
                    }}
                >
                    {loading ? (
                        Array.from({ length: 3 }).map((_, index) => (
                            <SwiperSlide key={index}>
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <Skeleton height={120} />
                                </div>
                            </SwiperSlide>
                        ))
                    ) : leagues.length === 0 ? (
                        <SwiperSlide>
                            <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                                <p className="text-[#667085] text-sm">There are no leagues yet.</p>
                            </div>
                        </SwiperSlide>
                    ) : (
                        leagues.map((item, index) => (
                            <SwiperSlide key={index}>
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="w-full border-b border-[#EAEBF0] flex p-3 justify-between items-center font-medium font-Manrope text-sm text-black">
                                        <p className="font-bold text-[#317333]">League</p>
                                        <Link
                                            to={`/leagues?id=${encodeId(item.id)}&name=${encodeURIComponent(item.name)}`}
                                            className="text-[#317333] font-bold font-Manrope text-sm"
                                        >
                                            View Details
                                        </Link>
                                    </div>
                                    <div className="w-full border-b border-[#EAEBF0] flex p-3 justify-between items-center font-medium font-Manrope text-sm text-[#5F6D7E]">
                                        <span className="flex items-center whitespace-nowrap">
                                            <p>{item.name}</p>
                                        </span>
                                    </div>
                                    <div className="w-full border-b border-[#EAEBF0] flex p-3 justify-between items-center font-medium font-Manrope text-sm text-[#5F6D7E]">
                                        <p className="font-bold text-black">Location:</p>
                                        <p>{getLocationLabel(item)}</p>
                                    </div>
                                    <div className="w-full border-b border-[#EAEBF0] flex p-3 justify-between items-center font-medium font-Manrope text-sm text-[#5F6D7E]">
                                        <p className="font-bold text-black">Start Date:</p>
                                        <p>
                                            {item.start_date || item.created_at ?
                                                new Date(item.start_date || item.created_at || "").toLocaleDateString() :
                                                "N/A"
                                            }
                                        </p>
                                    </div>
                                    <div className="w-full border-b border-[#EAEBF0] flex p-3 justify-between items-center font-medium font-Manrope text-sm text-[#5F6D7E]">
                                        <p className="font-bold text-black">Status:</p>
                                        <span
                                            className={`px-2 py-1 rounded-full text-xs font-semibold ${item.status === "active" || item.is_active
                                                ? "bg-green-100 text-green-800"
                                                : "bg-gray-100 text-gray-800"
                                                }`}
                                        >
                                            {item.status === "active" || item.is_active ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))
                    )}
                </Swiper>
                <div className="flex justify-center mt-4">
                    <div className="leagues-pagination"></div>
                </div>

                {leagues.length > 5 && (
                    <Link
                        to="/leagues"
                        className="mt-8 md:mt-16 py-3 px-[18px] bg-[#317333] rounded-[6px] font-Manrope text-[#fff] font-semibold text-[15px] mx-auto"
                    >
                        View more
                    </Link>
                )}
            </div>
        </>
    );
};

export default LeagueLanding;
